import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Instagram, Menu, X, Info } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const TEAL = "#0F8B8D";
const NAME = "City Smile Dental & Associates";
const FB = "https://www.facebook.com/p/City-Smile-Dental-61556926867480/";
const IG = "https://www.instagram.com/citysmiledental_dumaguete/";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function CitySmileConcept() {
  const [open, setOpen] = useState(false);
  const links = [["Services", "#services"], ["Precision", "#precision"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#111827", fontFamily: "'Sora', sans-serif" }} data-testid="citysmile-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@400;500;600;700&display=swap'); .cs-mono{font-family:'Space Mono',monospace;}`}</style>

      <div data-testid="dental-disclaimer" className="bg-black text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2"><Info size={13} className="text-white/70" /> Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b border-black/10">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
          <a href="#top" data-testid="cs-logo" className="flex items-center gap-2">
            <img src="/dental/city-smile-dental-concept/logo.jpg" alt={NAME} className="h-14 sm:h-16" />
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="cs-mono text-xs uppercase tracking-wide text-black/60 hover:text-black transition-colors">{l}</a>)}
          </div>
          <a href="#visit" data-testid="cs-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: TEAL }}>Book a Visit</a>
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="cs-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="lg:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — grid/blueprint precision motif */}
      <section className="relative py-14 sm:py-20 border-b border-black/10" data-testid="cs-hero" style={{ backgroundImage: "linear-gradient(rgba(15,139,141,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,139,141,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-[1fr_.72fr] gap-10 items-center">
          <motion.div {...fadeUp}>
            <span className="cs-mono text-xs uppercase tracking-[0.3em]" style={{ color: TEAL }}>Daro, Dumaguete City</span>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">Precise, modern<br />dental care.</h1>
            <p className="mt-6 text-lg text-black/60 max-w-lg">Braces, teeth whitening and dental implants planned with precision — from consultation to the final result.</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#visit" data-testid="cs-hero-cta" className="px-8 py-3.5 text-sm font-semibold text-white" style={{ background: TEAL }}>Book a Visit</a>
              <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="cs-hero-message" className="px-8 py-3.5 text-sm font-semibold border-2 border-black/15">Message Us</a>
            </div>
            <p className="mt-8 cs-mono text-xs text-black/50">4.7★ RATING · 49 REVIEWS</p>
          </motion.div>
          <motion.figure {...fadeUp} className="bg-white p-6 border border-black/10 shadow-lg rotate-1"><img src="/dental/city-smile-dental-concept/logo.jpg" alt="City Smile Dental clinic identity" className="w-full object-contain" /><figcaption className="mt-4 cs-mono text-xs text-black/50">DARO · DUMAGUETE CITY</figcaption></motion.figure>
        </div>
      </section>

      {/* SERVICES — sparse numbered list */}
      <section id="services" className="max-w-4xl mx-auto px-5 sm:px-8 py-24">
        <motion.h2 {...fadeUp} className="cs-mono text-xs uppercase tracking-[0.3em] text-center mb-3" style={{ color: TEAL }}>What We Offer</motion.h2>
        <motion.h3 {...fadeUp} className="text-3xl font-bold text-center mb-14">Our Services</motion.h3>
        <div className="space-y-0 border-t border-black/10">
          {[
            { n: "01", t: "Braces / Orthodontics" },
            { n: "02", t: "Teeth Whitening" },
            { n: "03", t: "Dental Implants" },
            { n: "04", t: "General Dentistry" },
          ].map((s) => (
            <motion.div key={s.n} {...fadeUp} className="flex items-center justify-between py-6 border-b border-black/10">
              <span className="cs-mono text-sm" style={{ color: TEAL }}>{s.n}</span>
              <span className="text-xl font-semibold">{s.t}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRECISION — their own branded implant graphic */}
      <section id="precision" className="py-24" style={{ background: "#F4FBFB" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <motion.img {...fadeUp} src="/dental/city-smile-dental-concept/blueprint.jpg" alt="The Blueprint for your new smile — precision implant planning by City Smile Dental" className="w-full object-contain shadow-lg" />
          <motion.div {...fadeUp}>
            <span className="cs-mono text-xs uppercase tracking-[0.3em]" style={{ color: TEAL }}>Implant Planning</span>
            <h2 className="mt-4 text-3xl font-bold">The blueprint for your new smile</h2>
            <p className="mt-5 text-black/60 leading-relaxed">Dental lab work is prepared with precision, from the mold stage through to the final restoration.</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.img {...fadeUp} src="/dental/city-smile-dental-concept/lab-molds.jpg" alt="Dental lab work prepared for City Smile Dental patients" className="w-full max-w-md mx-auto object-cover shadow-md" />
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" className="max-w-5xl mx-auto px-5 sm:px-8 py-24 border-t border-black/10">
        <motion.h2 {...fadeUp} className="text-3xl font-bold text-center mb-14">What Patients Say</motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { text: "Currently getting braces here — pricier than some alternatives but the quality justifies it. Welcoming staff, gentle dentist, and very precise work.", author: "DingoFun4242", source: "Reddit r/dumaguete" },
            { text: "The dentist and assistants made me feel very comfortable, and the clinic was clean.", author: "cedcedced_", source: "Reddit r/dumaguete" },
            { text: "Teeth whitening results were excellent and the process was quick and painless.", author: "Facebook patient post" },
          ].map((q, i) => (
            <motion.div key={i} {...fadeUp} data-testid={`cs-testimonial-${i}`} className="p-7 border border-black/10">
              <p className="text-black/70 leading-relaxed">"{q.text}"</p>
              <p className="mt-4 cs-mono text-xs" style={{ color: TEAL }}>{q.author}{q.source ? ` · ${q.source}` : ""}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="py-24" style={{ background: "#0B1220" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl font-bold text-white">Book a Visit</h2>
            <ul className="mt-8 space-y-5 text-white/70">
              <li className="flex items-start gap-4"><MapPin size={20} style={{ color: TEAL }} className="mt-0.5" />Diego De La Viña Rd., Daro, Dumaguete City, Negros Oriental</li>
              <li className="flex items-start gap-4"><Phone size={20} style={{ color: TEAL }} className="mt-0.5" /><a href="tel:+639175033757" className="hover:underline">+63 917 503 3757</a></li>
              <li className="flex items-start gap-4"><Facebook size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="cs-facebook-link" className="hover:underline">City Smile Dental</a></li>
              <li className="flex items-start gap-4"><Instagram size={20} style={{ color: TEAL }} className="mt-0.5" /><a href={IG} target="_blank" rel="noopener noreferrer" data-testid="cs-instagram-link" className="hover:underline">citysmiledental_dumaguete</a></li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp} className="min-h-[320px]">
            <iframe title={`${NAME} map`} src={`https://www.google.com/maps?q=${encodeURIComponent("Diego De La Vina Rd Daro Dumaguete City")}&output=embed`} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </section>

      <footer className="py-10 bg-white border-t border-black/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-black/50">
          <p className="font-bold text-black">City Smile Dental &amp; Associates</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {NAME}. Confirm details directly with the clinic before booking.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="dental-sticky-contact" className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl" style={{ background: TEAL }}>Message City Smile</a>
      <ConceptCTAPill slug="city-smile-dental-concept" />
    </div>
  );
}
